import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAbility } from '@casl/react';
import { ArrowLeftRight, ArrowRight, Clock, Plus, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Can } from '../../components/Can';
import AccessRestricted from '../../components/AccessRestricted';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { TransferOrder, TransferStatus, Yard } from '../../types/stock.types';
import { transfersApi, yardsApi } from '../../utils/stock-api';
import { cn } from '../../components/ui/utils';
import TransferOrderFormDialog from './components/TransferOrderFormDialog';

const ALL = 'all';

const STATUS_FILTERS = [
    { value: ALL, label: 'All statuses' },
    { value: 'draft', label: 'Draft' },
    { value: 'packing', label: 'Packing' },
    { value: 'in_transit', label: 'In Transit' },
    { value: 'received', label: 'Received' },
    { value: 'ready_to_enter', label: 'Ready to Enter' },
    { value: 'transfer_entered', label: 'Transfer Entered' },
    { value: 'voided', label: 'Voided' },
];

const STATUS_STYLES: Record<TransferStatus, string> = {
    draft: 'text-slate-700 bg-slate-100',
    packing: 'text-blue-700 bg-blue-50',
    in_transit: 'text-amber-700 bg-amber-50 ring-1 ring-amber-200',
    received: 'text-emerald-700 bg-emerald-50',
    ready_to_enter: 'text-amber-700 bg-amber-50',
    transfer_entered: 'text-emerald-800 bg-emerald-100 ring-1 ring-emerald-200',
    voided: 'text-red-700 bg-red-50 ring-1 ring-red-200',
};

export const TransferStatusPill: React.FC<{ status: TransferStatus }> = ({ status }) => (
    <span className={cn(
        'inline-flex items-center text-xs font-medium px-2 py-1 rounded-md whitespace-nowrap capitalize',
        STATUS_STYLES[status] ?? 'text-slate-700 bg-slate-100'
    )}>
        {String(status).replace(/_/g, ' ')}
    </span>
);

/**
 * Transfer Orders — STOCK.md §7, a single GLOBAL list (STOCK-MENU.md §3).
 *
 * Not under a yard, because a transfer has two and belongs to neither. The server
 * filters to transfers where you can see the origin OR the destination — a Sydney member
 * must see gear heading to Sydney, which is the whole point of the module.
 *
 * Days-in-transit is surfaced because §16.2.6 calls material between yards the classic
 * loss vector: it belongs to nobody's stock on hand while it moves.
 */
export default function TransferOrdersPage() {
    const ability = useAbility(AbilityContext);
    const canRead = ability.can(ACTIONS.READ, RESOURCES.STOCK_TRANSFER_ORDER);
    const navigate = useNavigate();

    const [orders, setOrders] = useState<TransferOrder[]>([]);
    const [yards, setYards] = useState<Yard[]>([]);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState<string>(ALL);
    const [direction, setDirection] = useState<'all' | 'incoming' | 'outgoing'>('all');
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);

    const fetchOrders = useCallback(async () => {
        try {
            setLoading(true);
            setOrders(await transfersApi.list({
                status: status === ALL ? undefined : status,
                direction: direction === 'all' ? undefined : direction,
            }));
        } catch (err: any) {
            toast.error(err?.message || 'Failed to load transfer orders');
        } finally {
            setLoading(false);
        }
    }, [status, direction]);

    useEffect(() => { if (canRead) fetchOrders(); }, [canRead, fetchOrders]);

    useEffect(() => {
        if (!canRead) return;
        yardsApi.list({ status: 'open' }).then(setYards).catch(() => undefined);
    }, [canRead]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return orders;
        return orders.filter((o) =>
            [o.orderNumber, o.externalRef, o.originYard?.yardCode, o.destinationYard?.yardCode,
             o.originYard?.yardName, o.destinationYard?.yardName]
                .filter(Boolean)
                .some((v) => (v as string).toLowerCase().includes(q))
        );
    }, [orders, search]);

    if (!canRead) return <AccessRestricted />;

    const yardCell = (y: TransferOrder['originYard']) => (
        <div className="min-w-0">
            <span className="font-mono text-sm">{y?.yardCode ?? '–'}</span>
            {/* Branch is derived context (§7), shown as secondary text per STOCK-MENU §3. */}
            <span className="block text-xs text-muted-foreground truncate">
                {y?.branch?.name ?? ''}
            </span>
        </div>
    );

    return (
        <div className="space-y-4 md:space-y-6 p-2 md:p-0">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold">Transfer Orders</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Material moving between yards. Shown across every yard you can see, in
                        either direction.
                    </p>
                </div>
                <Can I={ACTIONS.CREATE} a={RESOURCES.STOCK_TRANSFER_ORDER}>
                    <Button
                        onClick={() => setDialogOpen(true)}
                        className="flex items-center gap-2 w-full md:w-auto bg-slate-900 hover:bg-slate-800 text-white border-0"
                    >
                        <Plus size={16} /><span>New Transfer</span>
                    </Button>
                </Can>
            </div>

            {/* §3 quick filters */}
            <Tabs value={direction} onValueChange={(v) => setDirection(v as typeof direction)} className="w-full">
                <TabsList className="bg-gray-100 p-1 rounded-full w-full md:w-fit">
                    <TabsTrigger value="all" className="rounded-full flex-1 md:flex-none px-4 py-2 data-[state=active]:bg-white">
                        All
                    </TabsTrigger>
                    <TabsTrigger value="incoming" className="rounded-full flex-1 md:flex-none px-4 py-2 data-[state=active]:bg-white">
                        Incoming to my yards
                    </TabsTrigger>
                    <TabsTrigger value="outgoing" className="rounded-full flex-1 md:flex-none px-4 py-2 data-[state=active]:bg-white">
                        Outgoing from my yards
                    </TabsTrigger>
                </TabsList>
            </Tabs>

            <div className="flex flex-col md:flex-row items-start md:items-center w-full gap-4 flex-wrap">
                <div className="relative w-full md:flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search by order number, yard or reference..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10 w-full"
                    />
                </div>
                <div className="w-full md:w-[200px]">
                    <Select value={status} onValueChange={setStatus}>
                        <SelectTrigger className="w-full bg-white"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {STATUS_FILTERS.map((s) => (
                                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <span className="text-sm text-muted-foreground">{filtered.length} records</span>
            </div>

            {loading ? (
                <div className="space-y-3 animate-pulse">
                    {[0, 1, 2].map((i) => <div key={i} className="h-14 bg-gray-300 rounded-md" />)}
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                    {yards.length < 2 ? (
                        <>
                            <p>No transfers.</p>
                            <p className="text-sm mt-1">
                                A transfer moves stock between two yards — you need at least two.
                            </p>
                        </>
                    ) : 'No transfer orders found'}
                </div>
            ) : (
                <>
                    <div className="md:hidden space-y-3">
                        {filtered.map((o) => (
                            <Card key={o.id} className="p-4 active:bg-accent/40"
                                  onClick={() => navigate(`/stock/transfers/${o.id}`)}>
                                <div className="flex items-start justify-between gap-3">
                                    <span className="font-mono font-medium">{o.orderNumber}</span>
                                    <TransferStatusPill status={o.status} />
                                </div>
                                <div className="mt-3 flex items-center gap-2 text-sm">
                                    <span className="font-mono">{o.originYard?.yardCode}</span>
                                    <ArrowRight size={14} className="text-muted-foreground" />
                                    <span className="font-mono">{o.destinationYard?.yardCode}</span>
                                </div>
                                {o.daysInTransit !== null && o.daysInTransit !== undefined && (
                                    <p className="mt-2 text-xs text-amber-700 flex items-center gap-1.5">
                                        <Clock size={12} />{o.daysInTransit} day(s) in transit
                                    </p>
                                )}
                            </Card>
                        ))}
                    </div>

                    <Card className="hidden md:block">
                        <CardContent className="p-3">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="font-semibold text-slate-900">Order Number</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Origin</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Destination</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Departed</TableHead>
                                        <TableHead className="font-semibold text-slate-900">In transit</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Lines</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Status</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filtered.map((o) => (
                                        <TableRow key={o.id} onClick={() => navigate(`/stock/transfers/${o.id}`)}
                                                  className="cursor-pointer">
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <ArrowLeftRight size={16} className="text-muted-foreground" />
                                                    <span className="font-mono">{o.orderNumber}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell>{yardCell(o.originYard)}</TableCell>
                                            <TableCell>{yardCell(o.destinationYard)}</TableCell>
                                            <TableCell>{o.departedAt ? String(o.departedAt).slice(0, 10) : '–'}</TableCell>
                                            <TableCell>
                                                {o.daysInTransit !== null && o.daysInTransit !== undefined ? (
                                                    <span className={cn(
                                                        'text-sm',
                                                        o.daysInTransit >= 3 ? 'text-amber-700 font-medium' : 'text-muted-foreground'
                                                    )}>
                                                        {o.daysInTransit}d
                                                    </span>
                                                ) : <span className="text-muted-foreground">–</span>}
                                            </TableCell>
                                            <TableCell>{o.lineCount ?? 0}</TableCell>
                                            <TableCell><TransferStatusPill status={o.status} /></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </>
            )}

            <TransferOrderFormDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                yards={yards}
                onCreated={(order) => navigate(`/stock/transfers/${order.id}`)}
            />
        </div>
    );
}
